import { Dialog } from "@material-ui/core";
import {
  Box,
  Button,
  DatePicker,
  Field,
  Form,
  Grid,
  Notification,
  Select,
  TextField,
  Typography,
} from "components";
import dayjs from "dayjs";
import { compose, withAuthorize, withFormik, withHooks } from "enhancers";
import newWithTranslation from "enhancers/withTranslation";
import appStore from "stores/appStore";
import styled from "styled-components";
import { AppColor } from "theme/app-color";
import { formatDateWithBuddhistYear, gql, Yup } from "utils/helper";
import { waiting_requests_id } from "../paymentCycles/PaymentCycleTable";
import { EnumClaimRequestStatus } from "constants/enums/claim-request-status";
import { get, isNil, isUndefined } from "lodash";
import { reportNameMapper } from "constants/objects/excel_report_name_mapper";

// Type สำหรับ field names
type FieldName =
  | "department"
  | "paymentCycle"
  | "status"
  | "employee"
  | "relationWithOwner"
  | "payRound"
  | "createAtDate"
  | "budgetYear";

// Feature: Field Visibility Configuration
const FIELD_VISIBILITY: Record<string, FieldName[]> = {
  [reportNameMapper.report17]: ["budgetYear"], // ไม่แสดง field ใดๆ เพิ่มเติม
  [reportNameMapper.report18]: ["paymentCycle"], // ไม่แสดง field ใดๆ เพิ่มเติม
  "สรุปรายการเบิกทั้งหมด รายครั้ง": [
    "department",
    "paymentCycle",
    "status",
    "employee",
    "relationWithOwner",
    "payRound",
    "createAtDate",
  ],
  default: [
    "department",
    "paymentCycle",
    "employee",
    "relationWithOwner",
    "payRound",
    "createAtDate",
  ], // Default fields
};

const getVisibleFields = (reportType: string): FieldName[] =>
  FIELD_VISIBILITY[reportType] || FIELD_VISIBILITY.default;

const ResetFilterButton = styled(Box)`
  display: flex;
  align-items: center;
  text-decoration: underline;
  color: ${AppColor["Primary/Primary Text"]};
  :hover {
    cursor: pointer;
  }
`;

const yearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
  return years.map((year) => ({
    label: year.toString(),
    value: year.toString(),
  }));
};

const SummaryReportModal = (props: any) => {
  const visibleFields = getVisibleFields(props.values.reportType);

  return (
    <Dialog open={props.isOpen}>
      <Form>
        <Box style={{ minWidth: "600px" }}>
          <Box display="flex" justifyContent="space-between" mb={4}>
            <Typography variant="h3">{"สร้างรายงาน"}</Typography>
          </Box>
          <Grid container spacing={0}>
            {/* Always visible fields */}
            <Grid item xs={12} mb={4}>
              <Field
                component={TextField}
                name="reportName"
                label="ชื่อรายงาน"
                placeholder="ชื่อรายงาน"
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} mb={4}>
              <Field
                component={Select}
                name="reportType"
                type="text"
                label="ประเภทรายงาน"
                placeholder="ประเภทรายงาน"
                options={props.reportTypeOptions}
                fullWidth
                required
              />
            </Grid>

            {/* Conditional fields based on configuration */}
            {visibleFields.includes("department") && (
              <Grid item xs={12} mb={4}>
                <Field
                  component={Select}
                  name="department"
                  type="text"
                  label="ฝ่าย/สังกัด"
                  placeholder="สังกัด"
                  options={props.departmentOptions}
                  fullWidth
                  required
                  disableClearable={true}
                />
              </Grid>
            )}

            {visibleFields.includes("paymentCycle") && (
              <Grid item xs={12} mb={4}>
                <Field
                  component={Select}
                  name="paymentCycleId"
                  label="รอบการจ่ายเงิน"
                  placeholder="รอบการจ่ายเงิน"
                  options={props.paymentCycleOptions}
                  fullWidth
                  required={props.isPaymentCycleRequired}
                />
              </Grid>
            )}

            {visibleFields.includes("status") && (
              <Grid item xs={12} mb={4}>
                <Field
                  component={Select}
                  name="status"
                  label="สถานะ"
                  placeholder="สถานะ"
                  options={props.statusOptions}
                  fullWidth
                  disabled={props.isStatusFieldDisabled}
                />
              </Grid>
            )}

            {visibleFields.includes("employee") && (
              <Grid item xs={12} mb={4}>
                <Field
                  component={Select}
                  name="employee"
                  type="text"
                  label="พนักงานเจ้าของคำร้อง"
                  placeholder="พนักงานเจ้าของคำร้อง"
                  options={props.employeeGroupsOptions}
                  fullWidth
                />
              </Grid>
            )}

            {props.values.employee && (
              <Grid item xs={12} mb={4}>
                <Field
                  component={Select}
                  name="claimRequestTo"
                  type="text"
                  label="เบิกให้"
                  placeholder="เบิกให้"
                  options={props.relationShipOptions}
                  fullWidth
                />
              </Grid>
            )}

            {(visibleFields.includes("relationWithOwner") ||
              visibleFields.includes("payRound")) && (
              <Grid container spacing={6} mb={4}>
                {visibleFields.includes("relationWithOwner") && (
                  <Grid item xs={12} sm={6}>
                    <Field
                      component={Select}
                      name="relationWithOwner"
                      type="text"
                      label="ความสัมพันธ์กับเจ้าของคำร้อง"
                      placeholder="ความสัมพันธ์กับเจ้าของคำร้อง"
                      options={props.relationWithOwnerOptions}
                      fullWidth
                      required
                    />
                  </Grid>
                )}
                {visibleFields.includes("payRound") && (
                  <Grid item xs={12} sm={6}>
                    <Field
                      component={Select}
                      name="payRound"
                      type="text"
                      label="ลักษณะการจ่าย"
                      placeholder="ลักษณะการจ่าย"
                      options={props.payRoundOptions}
                      fullWidth
                      required
                    />
                  </Grid>
                )}
              </Grid>
            )}

            {visibleFields.includes("createAtDate") && (
              <Grid container spacing={6}>
                <Grid item xs={12} sm={6}>
                  <Field
                    fast={false}
                    component={DatePicker}
                    name="startDate"
                    label="วันที่สร้างรายการ"
                    placeholder="วันที่สร้างรายการ"
                    fullWidth
                    maxDate={props.values.endDate}
                    required={props.isCreateAtFieldRequired}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    fast={false}
                    component={DatePicker}
                    name="endDate"
                    label="จนถึงวันที่"
                    placeholder="จนถึงวันที่"
                    fullWidth
                    minDate={props.values.startDate}
                    required={props.isCreateAtFieldRequired}
                  />
                </Grid>
              </Grid>
            )}

            {visibleFields.includes("budgetYear") && (
              <Grid item xs={12}>
                <Field
                  fast={false}
                  component={Select}
                  name="budgetYear"
                  label="ปี"
                  placeholder="ปี"
                  fullWidth
                  options={yearOptions()}
                  required={props.isCreateAtFieldRequired}
                />
              </Grid>
            )}
          </Grid>
          <Box
            style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
            mt={4}
          >
            <Button variant="outlined" onClick={props.clearFilter}>
              {props.t(".cancelButtonLabel")}
            </Button>
            <Button type="submit">{props.t(".okButtonLabel")}</Button>
          </Box>
        </Box>
      </Form>
    </Dialog>
  );
};

const API = {
  GET_DEPARTMENT: gql`
    query GET_DEPARTMENT {
      getDepartmentOption {
        label
        value
      }
    }
  `,
  GET_REPORTOPTIONS: gql`
    query GET_REPORTOPTIONS($id: String!) {
      getSummaryReportTypeOption(input: { id: $id }) {
        label
        value
      }
    }
  `,
  GET_EMPLOYEEOWNSREQUEST: gql`
    query GET_EMPLOYEEOWNSREQUEST($department: String) {
      getEmployeeOwnsRequestOption(input: { department: $department }) {
        label
        value
      }
    }
  `,
  GET_CLAIMREQUESTTO: gql`
    query GET_CLAIMREQUESTTO($employee: String) {
      getClaimRequestTo(input: { employee: $employee }) {
        label
        value
      }
    }
  `,
  GET_PAYROUND: gql`
    query GET_PAYROUND {
      getPayRoundOption {
        label
        value
      }
    }
  `,
  GET_RELATIONWITHOWNER: gql`
    query GET_RELATIONWITHOWNER {
      getrelationWithOwner {
        label
        value
      }
    }
  `,
  GEN_REPORT: gql`
    mutation GEN_REPORT(
      $reportName: String
      $reportType: String
      $startDate: String
      $endDate: String
      $claimRequestTo: String
      $employee: String
      $department: String
      $payRound: String
      $relationWithOwner: String
      $userId: String
      $paymentCycleId: String
      $status: String
      $budgetYear: String
    ) {
      generateReport(
        input: {
          reportName: $reportName
          reportType: $reportType
          startDate: $startDate
          endDate: $endDate
          claimRequestTo: $claimRequestTo
          employee: $employee
          department: $department
          payRound: $payRound
          relationWithOwner: $relationWithOwner
          userId: $userId
          paymentCycleId: $paymentCycleId
          status: $status
          budgetYear: $budgetYear
        }
      )
    }
  `,
  PAYMENT_REPORT_OPTIONS: gql`
    query PAYMENT_REPORT_OPTIONS {
      paymentCycleListForReport {
        id
        name
        paymentDate
      }
    }
  `,
};

const defaultClaimRequestTo = "self";

const enhancer = compose(
  withAuthorize(),
  withFormik({
    mapPropsToValues: () => ({
      reportName: "Report_" + dayjs(new Date()).format("DDMMYYYYHHmm"),
      reportType: "",
      department: "all",
      payRound: "all",
      relationWithOwner: "all",
      claimRequestTo: undefined,
      startDate: undefined,
      endDate: undefined,
      status: undefined,
    }),
    validationSchema: Yup.object().shape({
      reportName: Yup.string().required(".reportNameWarning").nullable(),
      reportType: Yup.string().required(".reportTypeWarning").nullable(),
      department: Yup.string().required(".reportDepartmentWarning").nullable(),
      relationWithOwner: Yup.string()
        .required(".relationWithOwnerWarning")
        .nullable(),
      // startDate: Yup.date().required(".startDateWarning").nullable(),
      // endDate: Yup.date().required(".endDateWarning").nullable(),
    }),
  }),
  newWithTranslation({
    prefix: "pages.main.reports.index.modal",
  }),
  withHooks((props: any, hooks: any) => {
    const {
      values,
      setFieldValue,
      isOpen,
      handleCloseModal,
      fetchData,
      t,
      resetForm,
    } = props;
    const {
      useQuery,
      useMemo,
      useEffect,
      useCallback,
      useHandleSubmit,
      useMutation,
    } = hooks;
    const { data: reportTypeOptions } = useQuery(API.GET_REPORTOPTIONS, {
      fetchPolicy: "network-only",
      variables: { id: appStore.state.currentUser.id },
    });
    const { data: department, loading } = useQuery(API.GET_DEPARTMENT, {
      fetchPolicy: "network-only",
    });
    const { data: employeeOptions } = useQuery(API.GET_EMPLOYEEOWNSREQUEST, {
      fetchPolicy: "network-only",
      variables: { department: values.department },
    });
    const { data: claimRequestOptions } = useQuery(API.GET_CLAIMREQUESTTO, {
      fetchPolicy: "network-only",
      variables: { employee: values.employee },
    });
    const { data: payRoundOptions } = useQuery(API.GET_PAYROUND, {
      fetchPolicy: "network-only",
    });
    const { data: relationWithOwnerOptions } = useQuery(
      API.GET_RELATIONWITHOWNER,
      {
        fetchPolicy: "network-only",
      }
    );
    const [genReport] = useMutation(API.GEN_REPORT, {
      onCompleted: () => {
        Notification.success(t(".generating"));
      },
    });
    const departmentOptions = useMemo(() => {
      return [{ label: "ทั้งหมด", value: "all" }].concat(
        department?.getDepartmentOption
      );
    }, [department, loading]);

    const { data: paymentCycleListData } = useQuery(
      API.PAYMENT_REPORT_OPTIONS,
      {
        fetchPolicy: "network-only",
      }
    );
    const paymentCycleOptions = useMemo(() => {
      const waitingOptions = [
        {
          label: t(".waitingOptions"),
          value: waiting_requests_id,
        },
      ];

      const paymentCycles = (
        paymentCycleListData?.paymentCycleListForReport ?? []
      ).map((cycle: any) => ({
        label: `[${formatDateWithBuddhistYear(
          cycle.paymentDate,
          "dd/MM/yyyy"
        )}] ${cycle.name}`,
        value: cycle.id,
      }));

      // ถ้าเป็น report18 ให้ไม่แสดง option "รอจัดการ"
      if (values.reportType === reportNameMapper.report18) {
        return paymentCycles;
      }

      return [...waitingOptions, ...paymentCycles];
    }, [paymentCycleListData?.paymentCycleListForReport, t, values.reportType]);

    useHandleSubmit(async (values: any) => {
      await genReport({
        variables: {
          ...values,
          paymentCycleId:
            values.paymentCycleId === waiting_requests_id
              ? null
              : values.paymentCycleId,
          userId: appStore.state.currentUser.id,
        },
      });
      resetForm();
      handleCloseModal();
      fetchData();
    }, []);

    const clearFilter = useCallback(async () => {
      resetForm();
      handleCloseModal();
    }, []);

    useEffect(() => {
      if (values.department) {
        setFieldValue("employee", "");
        setFieldValue("claimRequestTo", undefined);
      }
    }, [setFieldValue, values.department]);

    // Reset fields when report type changes (except reportName)
    useEffect(() => {
      if (values.reportType) {
        // Reset all fields except reportName
        setFieldValue("department", "all");
        setFieldValue("payRound", "all");
        setFieldValue("relationWithOwner", "all");
        setFieldValue("claimRequestTo", undefined);
        setFieldValue("startDate", undefined);
        setFieldValue("endDate", undefined);
        setFieldValue("status", undefined);
        setFieldValue("employee", "");
        setFieldValue("paymentCycleId", undefined);
        setFieldValue("budgetYear", undefined);
      }
    }, [setFieldValue, values.reportType]);

    const isCreateAtFieldRequired = useMemo(() => {
      if (values.paymentCycleId) return false;
      if (values.reportType === reportNameMapper.report17) return false;
      return true;
    }, [values.paymentCycleId, values.reportType]);

    const isPaymentCycleRequired = useMemo(() => {
      return values.reportType === reportNameMapper.report18;
    }, [values.reportType]);

    const relationShipOptions = useMemo(() => {
      const owner = { label: "ตัวเอง", value: defaultClaimRequestTo };
      const other = claimRequestOptions?.getClaimRequestTo || [];
      return [owner, ...other];
    }, [claimRequestOptions?.getClaimRequestTo]);

    const isStatusFieldDisabled = useMemo(() => {
      const paymentCycleId = get(values, "paymentCycleId");
      return paymentCycleId !== waiting_requests_id && !isNil(paymentCycleId);
    }, [values]);

    const statusOptions = useMemo(() => {
      const optionStatus = isStatusFieldDisabled
        ? [EnumClaimRequestStatus.APPROVED]
        : [
            EnumClaimRequestStatus.APPROVED,
            EnumClaimRequestStatus.REJECTED,
            EnumClaimRequestStatus.WAITING,
          ];
      return optionStatus.map((key) => ({
        label: t(`client.enum.claimRequestStatus.${key}`),
        value:
          EnumClaimRequestStatus[key as keyof typeof EnumClaimRequestStatus],
      }));
    }, [isStatusFieldDisabled, t]);

    useEffect(() => {
      if (isStatusFieldDisabled) {
        setFieldValue("status", EnumClaimRequestStatus.APPROVED);
      } else {
        setFieldValue("status", undefined);
      }
    }, [setFieldValue, isStatusFieldDisabled]);

    return {
      reportTypeOptions: reportTypeOptions?.getSummaryReportTypeOption,
      values,
      clearFilter,
      employeeGroupsOptions: employeeOptions?.getEmployeeOwnsRequestOption,
      isOpen,
      departmentOptions,
      payRoundOptions: payRoundOptions?.getPayRoundOption,
      relationShipOptions,
      relationWithOwnerOptions: relationWithOwnerOptions?.getrelationWithOwner,
      paymentCycleOptions,
      isCreateAtFieldRequired,
      isPaymentCycleRequired,
      statusOptions,
      isStatusFieldDisabled,
    };
  })
);

export default enhancer(SummaryReportModal);
